import * as msal from "@azure/msal-browser";
import { EventType as MsalEventType } from "@azure/msal-browser";
import { IMsalConfig } from "../../../common/models/msalConfig";
import { sleep } from "../../../common/utilities/Sleep";

class MsalAuthService
{
    private _config: IMsalConfig | undefined = undefined;
    private _hasBeenInitialized = false;
    //private _isCordova = false;

    //msal-browser for web
    private _account: msal.AccountInfo | null = null;
    private _msalInstance: msal.PublicClientApplication | undefined;

    private _eventCallback?: (event: MsalEventType) => void | undefined;
    private _callbackID: string | null = null;

    private _loginRequest = {
        scopes: this._config?.aadScopes ?? [],
        redirectUri: window.location.origin,
    } as msal.RedirectRequest;

    private _tokenRequest = {
        scopes: this._config?.aadScopes,        
        redirectUri: window.location.origin,
        forceRefresh: false // Set this to "true" to skip a cached token and go to the server to get a new token
    } as msal.RedirectRequest;

    private _initAsync = async (): Promise<string> => {

        if (!this._config) {
            throw Error("MsalAuthService: configuration is required by calling setConfig() before using this service.");
        }

        // //start by figuring out if we are using Cordova or not
        // const cordovaInWindow = ("cordova" in window);
        // console.log(`Cordova in window: ${cordovaInWindow}`);
        
        // const pluginsInCordova = cordovaInWindow && ("plugins" in window.cordova);

        // this._isCordova = pluginsInCordova && typeof (window.cordova.plugins.msalPlugin) !== "undefined";
        // console.log(`Cordova MSAL: ${this._isCordova}`);

        // if (this._isCordova) {

        //     return new Promise((resolve, reject) => {
        //         window.cordova.plugins.msalPlugin.startLogger(
        //             // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //             (logItem: any) => { console.log(logItem) },
        //             // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //             (err: any) => { console.log(err) },
        //             false,
        //             "VERBOSE"
        //         );

        //         window.cordova.plugins.msalPlugin.msalInit(
        //             () => {
        //                 this._hasBeenInitialized = true;
        //                 resolve("initialized");
        //             },
        //             // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //             (err: any) => {
        //                 console.log(err.toString());
        //                 reject(err.toString())
        //             },
        //             {
        //                 accountMode: "SINGLE",
        //                 //scopes: [appSettings.aadScopes]
        //                 scopes: this._config?.aadScopes,
        //             }
        //         );
        //     });
        // }
        // else {
            return new Promise(async (resolve, reject) => {

                await this._initialize();

                if (this._msalInstance && this._hasBeenInitialized) {
                    resolve("initialized");
                }
                else {
                    reject("failed to initialize");
                }
            });
        //}
    };


    private _delayedEventCallback = async (ev: msal.EventType) => {
        // allow app to complete initialization and prevent race condition
        // where view has not yet registered the callback
        let retries = 5;
        while (retries--) {

            await sleep(1000);

            if (this._eventCallback) {

                console.log('sending event call back now ');

                this._eventCallback(ev);

                return;
            }
        }
    }

    private _internalEventCallback = (message: msal.EventMessage) => {

        console.log(`event callback ${message.eventType.toString()}`);


        if (message.eventType === msal.EventType.ACQUIRE_TOKEN_FAILURE) {
            // token expired, force sign in 
            this._account = null;
            this._signInWeb();
        }

        if (this._eventCallback) {
            this._eventCallback(message.eventType);
        } else {

            // a callback has yet to be registered, give the calling app
            // a chance so the callback event is not missed
            const ev = message.eventType;

            this._delayedEventCallback(ev);
        }
    }

    private async _initialize() {

        console.log("Initializing msal!!!!!!!!!!");

        this._msalInstance = new msal.PublicClientApplication({
            auth: {
                clientId: this._config?.aadClientID,
                authority: "https://login.microsoftonline.com/organizations/"
            },
            cache: {
                cacheLocation: "localStorage", // This configures where your cache will be stored
                storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level, message, containsPii) => {
                        if (containsPii) {
                            return;
                        }
                        switch (level) {
                            case msal.LogLevel.Error:
                                console.error(message);
                                return;
                            case msal.LogLevel.Info:
                                console.info(message);
                                return;
                            case msal.LogLevel.Verbose:
                                console.debug(message);
                                return;
                            case msal.LogLevel.Warning:
                                console.warn(message);
                                return;
                        }
                    }
                }
            }
        } as msal.Configuration);

        if (this._msalInstance) {

            await this._msalInstance.initialize();

            this._hasBeenInitialized = true;

            this._callbackID = this._msalInstance.addEventCallback(this._internalEventCallback);

            const response = await this._msalInstance?.handleRedirectPromise();
            //console.log(`handleResponse ${!response ? 'bad' : JSON.stringify(response)}`);
            await this._handleResponse(response);
        }
    }

    private async _signInWeb() {
        
        if (!this._account) {
            let authResult: msal.AuthenticationResult | void | undefined;

            //see if we can get an account from the cache
            const storedAccounts = this._msalInstance?.getAllAccounts();
            if (storedAccounts) {
                const ssoAccount = storedAccounts.find(a => a.username);

                if (ssoAccount) {
                    const ssoRequest = {
                        ...this._loginRequest, 
                        loginHint: ssoAccount.username 
                    } as msal.AuthorizationUrlRequest;

                    console.log(`ssoRequest ${JSON.stringify(ssoRequest)}`);
                    
                    authResult = await this._msalInstance?.ssoSilent(ssoRequest)
                        .catch(error => {
                            //console.error("Silent Error: " + error);
                            console.warn(`MSAL ssoSilent: ${error}`);
                    });

                    if (authResult) {
                        this._account = authResult.account;
                    }
                }
            }
            if (!authResult) {

                console.log(`initializing with ${JSON.stringify(this._loginRequest)}`);
                await this._msalInstance?.loginRedirect(this._loginRequest);
                
                // authResult = await this._msalInstance?.loginPopup(this._loginRequest);
                // if (authResult) {
                //     this._account = authResult.account;
                // }
            }
        }
    }


    //private _activeSignIn = false;
    // private async _signInWeb() {
        
    //     if (!this._account) {
    //         let authResult: msal.AuthenticationResult | void;

    //         //see if we can get an account from the cache
    //         const storedAccounts = this._msalInstance?.getAllAccounts();
    //         if (storedAccounts) {
    //             const ssoAccount = storedAccounts.find(a => a.username);

    //             if (ssoAccount) {
    //                 const ssoRequest = {
    //                     ...this._loginRequest, 
    //                     loginHint: ssoAccount.username 
    //                 } as msal.AuthorizationUrlRequest;

    //                 console.log(`ssoRequest ${JSON.stringify(ssoRequest)}`);
                    
    //                 authResult = await this._msalInstance?.ssoSilent(ssoRequest)
    //                     .catch(error => {
    //                         //console.error("Silent Error: " + error);
    //                         console.warn(`MSAL ssoSilent: ${error}`);
    //                 });

    //                 if (authResult) {
    //                     this._account = authResult.account;
    //                 }
    //             }
    //         }
    //         if (!authResult) {

    //             console.log(`initializing with ${JSON.stringify(this._loginRequest)}`);
    //             await this._msalInstance?.loginRedirect(this._loginRequest);
                
    //             // authResult = await this._msalInstance?.loginPopup(this._loginRequest);
    //             // if (authResult) {
    //             //     this._account = authResult.account;
    //             // }
    //         }
    //     }
    // }

    private async _handleResponse(authResult: msal.AuthenticationResult | null) {

        console.log(`handleResponse: ${authResult?.account}`);

        //alert(`handleResponse!!!!: ${JSON.stringify(authResult)}`);

        if (authResult) {
            this._account = authResult.account;
        } else {
            const accounts = this._msalInstance?.getAllAccounts();
            // if (!accounts?.length) {
            //     this._signInWeb();
            // } else if (accounts?.length === 1) {
            //     this._account = accounts[0]
            // }
            if (accounts?.length === 1) {
                this._account = accounts[0]
            }

        }
    }

    private _signOutWeb() {

        const logoutRequest: msal.EndSessionRequest = {
            account: this._account ?? undefined
        };

        this._hasBeenInitialized = false;
        this._msalInstance?.logout(logoutRequest);
        if (this._callbackID) {
            this._msalInstance?.removeEventCallback(this._callbackID);
        }
        console.log('signout msal')
    }

    private async _getIDTokenWeb(): Promise<string | null> {

        const accounts = this._msalInstance?.getAllAccounts();

        console.log(`getIDToken: account: ${this._account} and accounts ${accounts?.length}`);

        if (!this._account && accounts?.length) {
            this._account = accounts[0];
        }

        const silentTokenRequest = { 
            ...this._tokenRequest, 
            account: this._account
        } as msal.SilentRequest;

        let result = await this._msalInstance?.acquireTokenSilent(silentTokenRequest)
                        .catch(async (error) => {
                            console.log("silent token acquisition fails.");

                            if (error instanceof msal.InteractionRequiredAuthError) {
                                //console.log("acquiring token using popup");
                                //result = await this._msalInstance?.acquireTokenPopup(this._tokenRequest)
                                console.log("acquiring token using redirect");
                                
                                // @@NOTE: This function will navigate away from the current browser window.
                                // It currently returns a Promise in order to reflect the asynchronous nature of the code running in this function. 
                                result = await this._msalInstance?.acquireTokenRedirect(this._tokenRequest)
                                .catch(error => {
                                    console.error(error);
                                });
                        } else {
                            console.error(error);
                        }
                    });

        if (result) {
            //console.log(`idToken: ${result.idToken}`);
            return result.idToken;
        } else {
            return null;
        }
    }

    private async _getAccessTokenWeb() : Promise<string | null> {

        const silentTokenRequest = { 
            ...this._tokenRequest, 
            account: this._account
        } as msal.SilentRequest;

        let result = await this._msalInstance?.acquireTokenSilent(silentTokenRequest)
                        .catch(async (error) => {
                            console.log("silent token acquisition fails.");

                            if (error instanceof msal.InteractionRequiredAuthError) {
                                //console.log("acquiring token using popup");
                                //result = await this._msalInstance?.acquireTokenPopup(this._tokenRequest)
                                //console.log("acquiring token using redirect");

                                // @@NOTE: This function will navigate away from the current browser window.
                                // It currently returns a Promise in order to reflect the asynchronous nature of the code running in this function. 
                                result = await this._msalInstance?.acquireTokenRedirect(this._tokenRequest)

                                .catch(error => {
                                    console.error(error);
                                });
                        } else {
                            console.error(error);
                        }
                    });

        if (result) {
            //console.log(`accessToken: ${result.accessToken}  idToken: ${result.idToken}`);
            return result.accessToken;
        } else {
            return null;
        }
    }

    // cordova app
    // private _signInCordova = async (): Promise<string> => {
    //     return new Promise((resolve, reject) => {
    //         window.cordova.plugins.msalPlugin.signInSilent(
    //             function (msg: string) {
    //                 resolve(msg);
    //             },
    //             function (err: string) {
    //                 // err probably says "No accounts found" but maybe other debugging info
    //                 // Don't show this to the user; just use it for debugging.
    //                 console.log(err)
    //                 // Here's where you either call the next prompt or wait for the user
    //                 window.cordova.plugins.msalPlugin.signInInteractive(
    //                     function (msg: string) {
    //                         // msg is your JWT for the account the user just signed into
    //                         // Also never use a preposition to end a sentence with
    //                         resolve(msg);
    //                     },
    //                     function (err: string) {
    //                         // Usually if we get an error it just means the user cancelled the
    //                         // signin process and closed the popup window. Handle this however
    //                         // you want, depending again if you want guest access or not.
    //                         reject(err);
    //                     }
    //                 );
    //             }
    //         );
    //     });
    // }

    // private _signOutCordova = () => {
    //     window.cordova.plugins.msalPlugin.signOut(
    //         function () {
    //             //myAfterSignoutBusinessLogic();
    //             //@@kg navigate to login view
    //         },
    //         // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //         function (err: any) {
    //             console.log(`cordova sign out ${err}`);
    //             //myAwesomeErrorHandler.handlerError(err);
    //         },

    //         this._account?.homeAccountId
    //     );
    // }

    // private _getAccessTokenCordova = async (): Promise<string> => {
    //     return this._signInCordova();
    // }

    public async signIn() {
        if (!this._hasBeenInitialized) {
            await this._initAsync();
        }

        //await (this._isCordova ? this._signInCordova() : this._signInWeb());
        await this._signInWeb();
    }

    public async getAccessToken() {
        if (!this._hasBeenInitialized) {
            await this._initAsync();
        }
        //return this._isCordova ? this._getAccessTokenCordova() : this._getAccessTokenWeb();
        return this._getAccessTokenWeb();
    }

    public signOut() {
        //this._isCordova ? this._signOutCordova() : this._signOutWeb();
        this._signOutWeb();
    }

    public setConfig(config: IMsalConfig): void {
        this._config = config;
    }

    public async initialize(): Promise<void> {
        await this._initAsync();
    }

    public async getIDToken() {
        if (!this._hasBeenInitialized) {
            await this._initAsync();
        }
        //return this._isCordova ? this._getAccessTokenCordova() : this._getIDTokenWeb();
        return this._getIDTokenWeb();
    }

    // register callback for events from msal redirect.
    public registerEventCallback(callback: (event: MsalEventType) => void) {

        this._eventCallback = callback;
    }

    // TEST FUNCTION ONLY
    public triggerEventCallback(event: MsalEventType) {
        console.warn('triggerEventCallback is ONLY to be used for testing purposes!');
        if (this._eventCallback) {
            this._eventCallback(event);
        }
    }
}

// if (isCordova) {
//     document.addEventListener("deviceready", function () {
//         initAsync();
//     }, false);

// } else {
//     _initialize();
// }

export { MsalEventType };
const msalAuthService = new MsalAuthService();
export default msalAuthService;

